import React, { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { Link } from "react-router-dom";
import { useAppToast } from "../../../context/toast";
import { useBulkUpdateVoiceRequestStatus } from "../../../api/queries/voice-requests/useBulkUpdateVoiceRequestStatus";
import { useVoiceRequests } from "../../../api/queries/voice-requests/useVoiceRequests";
import { useUpdateVoiceRequestStatus } from "../../../api/queries/voice-requests/useUpdateVoiceRequestStatus";
import {
  VoiceRequestItem,
  VoiceRequestType,
  VoiceRequestStatus,
} from "../../../api/types/voice-requests";
import styles from "./styles.module.css";

const statusOptions = [
  { label: "All statuses", value: "" },
  { label: "New", value: VoiceRequestStatus.NEW },
  { label: "In progress", value: VoiceRequestStatus.IN_PROGRESS },
  { label: "Solved", value: VoiceRequestStatus.SOLVED },
];

const typeOptions = [
  { label: "All types", value: "" },
  { label: "Ask question", value: VoiceRequestType.ASK_QUESTION },
  { label: "Connect with", value: VoiceRequestType.CONNECT_WITH },
  { label: "General question", value: VoiceRequestType.GENERAL_QUESTION },
];

const typeLabels: Record<string, string> = {
  [VoiceRequestType.ASK_QUESTION]: "Ask question",
  [VoiceRequestType.CONNECT_WITH]: "Connect",
  [VoiceRequestType.GENERAL_QUESTION]: "General",
};

const statusLabels: Record<string, string> = {
  [VoiceRequestStatus.NEW]: "New",
  [VoiceRequestStatus.IN_PROGRESS]: "In progress",
  [VoiceRequestStatus.SOLVED]: "Solved",
};

const statusSeverities: Record<
  string,
  "success" | "info" | "warning" | "danger" | "secondary" | "contrast"
> = {
  [VoiceRequestStatus.NEW]: "info",
  [VoiceRequestStatus.IN_PROGRESS]: "warning",
  [VoiceRequestStatus.SOLVED]: "success",
};

const toFilterValue = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object" && "value" in value) {
    const optionValue = (value as { value?: unknown }).value;
    if (typeof optionValue === "string") {
      return optionValue;
    }
  }

  return "";
};

const truncate = (value: string | null, max: number) => {
  if (!value) {
    return <span className="text-muted">—</span>;
  }

  return value.length <= max ? value : `${value.slice(0, max)}...`;
};

const shortToken = (token: string) =>
  token.length <= 12 ? token : `${token.slice(0, 6)}...${token.slice(-4)}`;

export default function VoiceRequestsList() {
  const toast = useAppToast();
  const [status, setStatus] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [bulkProcessingStatus, setBulkProcessingStatus] =
    useState<VoiceRequestStatus | null>(null);
  const [selectedRequests, setSelectedRequests] = useState<VoiceRequestItem[]>(
    [],
  );
  const [messageRequest, setMessageRequest] = useState<VoiceRequestItem | null>(
    null,
  );

  const filters = useMemo(
    () => ({
      status: status || undefined,
      type: type || undefined,
      q: search.trim() || undefined,
    }),
    [status, type, search],
  );

  const { data, isLoading, refetch } = useVoiceRequests(filters);
  const updateStatus = useUpdateVoiceRequestStatus();
  const bulkUpdateStatus = useBulkUpdateVoiceRequestStatus();

  const requests = data?.data || [];

  const bulkTargets = useMemo(
    () => ({
      inProgress: selectedRequests.filter(
        (request) => request.status !== VoiceRequestStatus.IN_PROGRESS,
      ),
      solved: selectedRequests.filter(
        (request) => request.status !== VoiceRequestStatus.SOLVED,
      ),
      new: selectedRequests.filter(
        (request) => request.status === VoiceRequestStatus.IN_PROGRESS,
      ),
    }),
    [selectedRequests],
  );

  const handleUpdateStatus = async (
    id: string,
    nextStatus: VoiceRequestStatus,
  ) => {
    setProcessingId(id);
    try {
      await updateStatus.mutateAsync({ id, status: nextStatus });
      refetch();
    } catch (error) {
      toast.ref?.current?.show({
        severity: "error",
        summary: "Could not update request status",
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleBulkUpdateStatus = async (
    nextStatus: VoiceRequestStatus,
    eligibleRequests: VoiceRequestItem[],
  ) => {
    if (eligibleRequests.length === 0) {
      toast.ref?.current?.show({
        severity: "warn",
        summary: "No selected requests can move to that status",
      });
      return;
    }

    setBulkProcessingStatus(nextStatus);
    try {
      await bulkUpdateStatus.mutateAsync({
        ids: eligibleRequests.map((request) => request.id),
        status: nextStatus,
      });
      setSelectedRequests([]);
      refetch();
    } catch (error) {
      toast.ref?.current?.show({
        severity: "error",
        summary: "Could not update selected requests",
      });
    } finally {
      setBulkProcessingStatus(null);
    }
  };

  const typeTemplate = (rowData: VoiceRequestItem) => (
    <span className={styles.typePill}>
      {typeLabels[rowData.type] ?? rowData.type}
    </span>
  );

  const statusTemplate = (rowData: VoiceRequestItem) => (
    <Tag
      value={statusLabels[rowData.status] ?? rowData.status}
      severity={statusSeverities[rowData.status] ?? "secondary"}
      rounded
    />
  );

  const accountTemplate = (rowData: VoiceRequestItem) => (
    <div className={styles.accountCell}>
      <span className={styles.accountName}>{rowData.accountName}</span>
      <span className={styles.storyTypeName}>{rowData.storyTypeName}</span>
      <span className={styles.tokenChip} title={rowData.storyToken}>
        Token {shortToken(rowData.storyToken)}
      </span>
    </div>
  );

  const senderTemplate = (rowData: VoiceRequestItem) => {
    const name = `${rowData.senderFirstName ?? ""} ${
      rowData.senderLastName ?? ""
    }`.trim();
    return (
      <div className={styles.senderCell}>
        <span className={styles.senderName}>{name || "—"}</span>
        <span className={styles.senderEmail}>{rowData.senderEmail}</span>
      </div>
    );
  };

  const profileTemplate = (rowData: VoiceRequestItem) => {
    if (!rowData.profileId) {
      return <span className="text-muted">—</span>;
    }

    const profileName = rowData.profileName?.trim() || rowData.profileId;

    return (
      <Link
        to={`/people/${rowData.profileId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {profileName}
      </Link>
    );
  };

  const dateTemplate = (rowData: VoiceRequestItem) =>
    new Date(rowData.createdAt).toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });

  const topicTemplate = (rowData: VoiceRequestItem) => (
    <div className={styles.topicCell}>
      <span className={styles.topicText}>{truncate(rowData.topic, 42)}</span>
      {rowData.subject && (
        <span className={styles.subjectText}>
          {truncate(rowData.subject, 54)}
        </span>
      )}
    </div>
  );

  const messageTemplate = (rowData: VoiceRequestItem) => (
    <Button
      label={rowData.message ? "View message" : "No message"}
      icon="pi pi-comment"
      size="small"
      severity="secondary"
      text
      disabled={!rowData.message}
      onClick={() => setMessageRequest(rowData)}
    />
  );

  const actionTemplate = (rowData: VoiceRequestItem) => {
    const isLoadingRow = processingId === rowData.id;

    if (rowData.status === VoiceRequestStatus.NEW) {
      return (
        <div className="flex gap-2">
          <Button
            label="Mark In Progress"
            size="small"
            icon="pi pi-play"
            severity="secondary"
            loading={isLoadingRow}
            disabled={isLoadingRow}
            onClick={() =>
              handleUpdateStatus(rowData.id, VoiceRequestStatus.IN_PROGRESS)
            }
          />
          <Button
            label="Mark Solved"
            size="small"
            icon="pi pi-check"
            loading={isLoadingRow}
            disabled={isLoadingRow}
            onClick={() =>
              handleUpdateStatus(rowData.id, VoiceRequestStatus.SOLVED)
            }
          />
        </div>
      );
    }

    if (rowData.status === VoiceRequestStatus.IN_PROGRESS) {
      return (
        <div className="flex gap-2">
          <Button
            label="Back to New"
            size="small"
            severity="secondary"
            icon="pi pi-undo"
            loading={isLoadingRow}
            disabled={isLoadingRow}
            onClick={() =>
              handleUpdateStatus(rowData.id, VoiceRequestStatus.NEW)
            }
          />
          <Button
            label="Mark Solved"
            size="small"
            icon="pi pi-check"
            loading={isLoadingRow}
            disabled={isLoadingRow}
            onClick={() =>
              handleUpdateStatus(rowData.id, VoiceRequestStatus.SOLVED)
            }
          />
        </div>
      );
    }

    return (
      <Button
        label="Reopen"
        size="small"
        severity="secondary"
        icon="pi pi-refresh"
        loading={isLoadingRow}
        disabled={isLoadingRow}
        onClick={() =>
          handleUpdateStatus(rowData.id, VoiceRequestStatus.IN_PROGRESS)
        }
      />
    );
  };

  return (
    <div className="col-12">
      <div className={styles.header}>
        <div>
          <h1 className="m-0">Voice Requests</h1>
          <p className={styles.subtitle}>
            Review inbound student questions and connection requests.
          </p>
        </div>

        <div className={styles.filters}>
          <Dropdown
            value={status}
            options={statusOptions}
            onChange={(e) => setStatus(toFilterValue(e.value))}
            placeholder="Status"
            className={styles.filterControl}
          />
          <Dropdown
            value={type}
            options={typeOptions}
            onChange={(e) => setType(toFilterValue(e.value))}
            placeholder="Type"
            className={styles.filterControl}
          />
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sender/email/message..."
            className={styles.searchControl}
          />
        </div>
      </div>

      {selectedRequests.length > 0 && (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>
            {selectedRequests.length} selected
          </span>
          <div className={styles.bulkActions}>
            <Button
              label={`Mark In Progress (${bulkTargets.inProgress.length})`}
              icon="pi pi-play"
              size="small"
              severity="secondary"
              disabled={bulkTargets.inProgress.length === 0}
              loading={bulkProcessingStatus === VoiceRequestStatus.IN_PROGRESS}
              onClick={() =>
                handleBulkUpdateStatus(
                  VoiceRequestStatus.IN_PROGRESS,
                  bulkTargets.inProgress,
                )
              }
            />
            <Button
              label={`Mark Solved (${bulkTargets.solved.length})`}
              icon="pi pi-check"
              size="small"
              disabled={bulkTargets.solved.length === 0}
              loading={bulkProcessingStatus === VoiceRequestStatus.SOLVED}
              onClick={() =>
                handleBulkUpdateStatus(
                  VoiceRequestStatus.SOLVED,
                  bulkTargets.solved,
                )
              }
            />
            <Button
              label={`Back to New (${bulkTargets.new.length})`}
              icon="pi pi-undo"
              size="small"
              severity="secondary"
              disabled={bulkTargets.new.length === 0}
              loading={bulkProcessingStatus === VoiceRequestStatus.NEW}
              onClick={() =>
                handleBulkUpdateStatus(VoiceRequestStatus.NEW, bulkTargets.new)
              }
            />
          </div>
        </div>
      )}

      <DataTable
        value={requests}
        loading={isLoading}
        selectionMode="multiple"
        selection={selectedRequests}
        onSelectionChange={(event) =>
          setSelectedRequests(event.value as VoiceRequestItem[])
        }
        dataKey="id"
        paginator
        rows={25}
        emptyMessage="No requests found"
        className={`p-datatable-sm ${styles.table}`}
        stripedRows
        rowHover
        responsiveLayout="scroll"
      >
        <Column
          selectionMode="multiple"
          headerStyle={{ width: "3rem" }}
          style={{ width: "3rem" }}
        />
        <Column
          header="Created"
          body={dateTemplate}
          sortable
          style={{ minWidth: "150px" }}
        />
        <Column
          field="status"
          header="Status"
          body={statusTemplate}
          sortable
          style={{ minWidth: "120px" }}
        />
        <Column
          header="Type"
          body={typeTemplate}
          field="type"
          sortable
          style={{ minWidth: "130px" }}
        />
        <Column
          header="Account"
          body={accountTemplate}
          style={{ minWidth: "260px" }}
        />
        <Column
          header="Request"
          body={topicTemplate}
          style={{ minWidth: "260px" }}
        />
        <Column
          header="Profile"
          body={profileTemplate}
          style={{ minWidth: "160px" }}
        />
        <Column
          header="Sender"
          body={senderTemplate}
          style={{ minWidth: "220px" }}
        />
        <Column
          header="Message"
          body={messageTemplate}
          style={{ width: "130px" }}
        />
        <Column
          header="Actions"
          body={actionTemplate}
          style={{ minWidth: "190px" }}
        />
      </DataTable>

      <Dialog
        header={messageRequest?.subject || messageRequest?.topic || "Message"}
        visible={messageRequest !== null}
        onHide={() => setMessageRequest(null)}
        style={{ width: "min(720px, 92vw)" }}
        modal
      >
        {messageRequest && (
          <div className={styles.messageDialog}>
            <div className={styles.messageMeta}>
              <span>{messageRequest.accountName}</span>
              <span>{senderTemplate(messageRequest)}</span>
            </div>
            <div className={styles.messageBody}>{messageRequest.message}</div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
